"use client";

import { isIdentityGraphClientEnabled } from "@repo/identity";
import { orpcClient } from "@shared/lib/orpc-client";
import * as Posthog from "../provider/posthog";
import { hasAnalyticsCaptureConsent } from "./analytics-consent-gate";
import { ActiveAnalyticsProvider } from "./resolve-provider";

const SERVER_RECORDED_KEY_PREFIX = "sq:identity:posthog-link-server:";

interface PersistPosthogDistinctIdLinkParams {
	sqid: string;
	getCurrentDistinctId: () => Promise<string | null>;
	priorDistinctId?: string | null;
}

async function resolveDistinctIdForPosthogLink(
	params: PersistPosthogDistinctIdLinkParams,
): Promise<string | null> {
	const sqid = params.sqid.trim();
	if (!sqid) {
		return null;
	}

	if (ActiveAnalyticsProvider === Posthog) {
		const fromPosthog = (await params.getCurrentDistinctId())?.trim();
		if (fromPosthog) {
			return fromPosthog;
		}
	}

	return sqid;
}

/**
 * Best-effort persistence of PostHog distinct id ↔ sqid linkage on the identity graph.
 * Non-blocking: failures must not break bootstrap or session identify flows.
 */
export async function persistPosthogDistinctIdLink(
	params: PersistPosthogDistinctIdLinkParams,
): Promise<void> {
	if (!isIdentityGraphClientEnabled() || !hasAnalyticsCaptureConsent()) {
		return;
	}

	const sqid = params.sqid.trim();
	if (!sqid) {
		return;
	}

	const distinctId = await resolveDistinctIdForPosthogLink(params);
	if (!distinctId) {
		return;
	}

	const dedupeKey = `${SERVER_RECORDED_KEY_PREFIX}${sqid}:${distinctId}`;
	if (
		typeof window !== "undefined" &&
		window.sessionStorage.getItem(dedupeKey) === "1"
	) {
		return;
	}

	const priorDistinctId = params.priorDistinctId?.trim();
	const priorDistinctIds =
		priorDistinctId && priorDistinctId !== distinctId
			? [priorDistinctId]
			: undefined;

	const result = await orpcClient.identity.recordPosthogLink({
		sqid,
		distinctId,
		priorDistinctIds,
	});
	if (result.ok && typeof window !== "undefined") {
		window.sessionStorage.setItem(dedupeKey, "1");
	}
}
